// packages/app/src/components/tokens/TokensPage.tsx
//
// Self-service UI for Personal Access Tokens, backed by the `tokens` plugin
// (packages/backend/src/modules/tokens). A PAT lets a user call this portal's
// own API from Postman, curl or CI without an interactive Okta login.
//
// Layout follows Bitbucket's "Repository settings -> Access tokens" page: a
// listing with a "Create access token" button, a per-row action menu, and
// creation in a modal. The raw token is shown in a one-time reveal modal and
// then dropped from component state — GET /api/auth/tokens does not return the value
// or any part of it, so nothing on this page can show it again.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {
  useApi,
  identityApiRef,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';

// ─── Types (mirror of the router's response shapes) ──────────────────────────

interface PatSummary {
  id: string;
  name: string;
  createdAt: string;
  /** null when the token never expires. */
  expiresAt: string | null;
  lastUsedAt: string | null;
  expired: boolean;
}

interface CreatedToken {
  id: string;
  name: string;
  token: string;
  createdAt: string;
  expiresAt: string | null;
  note: string;
  /** Present on a rotate response only. */
  rotatedFrom?: string;
  previousRevoked?: boolean;
}

// Mirrors TOKEN_NAME_PATTERN in packages/backend/src/modules/scaffolder/
// validation.ts. Duplicated by the same convention as serviceKeyValidation.ts —
// there is no shared package between packages/app and packages/backend.
const TOKEN_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 ._-]{0,99}$/;

// Matches tokens.maxTtlDays / tokens.defaultTtlDays in app-config.yaml. The
// backend rejects anything outside this rather than clamping, so the picker
// offers only values it will accept.
//
// `null` is sent for "never expires" and is accepted unless the deployment sets
// tokens.allowNeverExpires: false — in which case the backend rejects it with a
// message this page surfaces as-is.
const TTL_OPTIONS: Array<{ value: number | null; label: string }> = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: null, label: 'Never expires' },
];
const DEFAULT_TTL: number | null = 30;

/** The <select> cannot hold null, so "never" travels as this sentinel. */
const NEVER = 'never';

function ttlToOption(ttl: number | null): string {
  return ttl === null ? NEVER : String(ttl);
}

function optionToTtl(value: string): number | null {
  return value === NEVER ? null : Number(value);
}

/** Stands in for the value wherever a token is referenced after creation. */
const MASKED_TOKEN = 'dtpat_••••••••••••••••••••';

// ─── Styles (same visual language as DashboardPage) ──────────────────────────

const S: Record<string, CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    background: '#ffffff',
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    overflow: 'hidden',
  },
  badge: {
    fontSize: 12,
    fontWeight: 400,
    padding: '4px 12px',
    borderRadius: 20,
    background: '#e8f0fe',
    color: '#1967d2',
    border: '1px solid #c5d8fc',
  },
  body: { padding: 24, overflowY: 'auto', flex: 1 },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    marginBottom: 20,
  },
  card: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    background: '#ffffff',
  },
  btn: {
    padding: '8px 16px',
    borderRadius: 6,
    border: '1px solid #1967d2',
    background: '#1967d2',
    color: '#ffffff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  btnDisabled: {
    background: '#b8ccef',
    borderColor: '#b8ccef',
    cursor: 'not-allowed',
  },
  btnGhost: {
    padding: '7px 14px',
    borderRadius: 6,
    border: '1px solid #cccccc',
    background: '#ffffff',
    color: '#333333',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnDangerSolid: {
    padding: '7px 18px',
    borderRadius: 6,
    border: '1px solid #c5221f',
    background: '#c5221f',
    color: '#ffffff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnDangerDisabled: {
    background: '#e2a3a2',
    borderColor: '#e2a3a2',
    cursor: 'not-allowed',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left',
    fontSize: 13,
  },
  th: {
    background: '#f5f5f5',
    padding: '12px 16px',
    fontWeight: 600,
    color: '#333333',
    borderBottom: '1px solid #e0e0e0',
  },
  td: {
    padding: '12px 16px',
    color: '#555555',
    borderBottom: '1px solid #f0f0f0',
  },
  tdName: {
    padding: '12px 16px',
    color: '#111111',
    fontWeight: 500,
    borderBottom: '1px solid #f0f0f0',
  },
  mono: { fontFamily: "'SF Mono', Menlo, Consolas, monospace", fontSize: 12 },
  center: { padding: 32, textAlign: 'center', color: '#888888', fontSize: 13 },
  errBox: {
    padding: '10px 14px',
    borderRadius: 6,
    background: '#fce8e6',
    border: '1px solid #f5c2c0',
    color: '#c5221f',
    fontSize: 13,
    marginBottom: 16,
  },
  pill: {
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 12,
    background: '#e6f4ea',
    color: '#137333',
  },
  pillExpired: { background: '#fce8e6', color: '#c5221f' },
  pillNever: { background: '#f1f3f4', color: '#5f6368' },

  // ── Row action menu ──
  actionsCell: {
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
    position: 'relative',
    textAlign: 'right',
  },
  kebab: {
    width: 34,
    height: 28,
    borderRadius: 6,
    border: '1px solid #cccccc',
    background: '#ffffff',
    color: '#333333',
    fontSize: 15,
    fontWeight: 700,
    lineHeight: '14px',
    cursor: 'pointer',
  },
  menu: {
    position: 'absolute',
    right: 16,
    top: 44,
    minWidth: 150,
    background: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
    zIndex: 20,
    overflow: 'hidden',
    textAlign: 'left',
  },
  menuItem: {
    display: 'block',
    width: '100%',
    padding: '9px 14px',
    border: 'none',
    background: 'none',
    textAlign: 'left',
    fontSize: 13,
    color: '#333333',
    cursor: 'pointer',
  },
  menuItemDanger: { color: '#c5221f' },

  // ── Modal ──
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 16,
  },
  modal: {
    background: '#ffffff',
    borderRadius: 10,
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    padding: '24px 28px 20px 28px',
    width: '100%',
    maxWidth: 540,
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: 600,
    color: '#111111',
    margin: '0 0 6px 0',
  },
  modalBlurb: {
    fontSize: 13,
    color: '#555555',
    lineHeight: 1.6,
    margin: '0 0 18px 0',
  },
  modalActions: {
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
    marginTop: 22,
  },
  field: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 },
  label: { fontSize: 12, fontWeight: 600, color: '#333333' },
  input: {
    padding: '8px 12px',
    borderRadius: 6,
    border: '1px solid #cccccc',
    fontSize: 13,
    outline: 'none',
    background: '#ffffff',
    color: '#333333',
    boxSizing: 'border-box',
    width: '100%',
  },
  hint: { fontSize: 11, color: '#888888' },
  hintError: { fontSize: 11, color: '#c5221f' },
  hintWarn: { fontSize: 11, color: '#a8660d', lineHeight: 1.5 },

  // ── One-time reveal ──
  warnBanner: {
    padding: '10px 14px',
    borderRadius: 6,
    background: '#fef7e0',
    border: '1px solid #f6c344',
    color: '#7a5900',
    fontSize: 13,
    lineHeight: 1.5,
    marginBottom: 14,
  },
  revealValue: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    background: '#f8f9fa',
    border: '1px solid #dadce0',
    borderRadius: 6,
    padding: '10px 12px',
  },
  revealToken: {
    flex: 1,
    fontFamily: "'SF Mono', Menlo, Consolas, monospace",
    fontSize: 13,
    color: '#111111',
    wordBreak: 'break-all',
  },

  // ── Details list ──
  dl: { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px 16px' },
  dt: { fontSize: 12, fontWeight: 600, color: '#333333' },
  dd: { fontSize: 13, color: '#555555', margin: 0, wordBreak: 'break-all' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** pg hands TIMESTAMPTZ back as a Date that the router stringifies; be lenient. */
function fmtDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function daysUntil(value: string): number | null {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86_400_000);
}

/**
 * The TTL a token was created with, so the rotate dialog can default to the
 * same policy. Derived from the two timestamps rather than stored, and snapped
 * to the nearest offered option — a token minted before an option list change
 * still lands on something the backend will accept.
 */
function originalTtl(token: PatSummary): number | null {
  if (!token.expiresAt) return null;
  const created = new Date(token.createdAt).getTime();
  const expires = new Date(token.expiresAt).getTime();
  if (Number.isNaN(created) || Number.isNaN(expires)) return DEFAULT_TTL;

  const days = Math.round((expires - created) / 86_400_000);
  const offered = TTL_OPTIONS.map(o => o.value).filter(
    (v): v is number => v !== null,
  );
  return offered.reduce((best, v) =>
    Math.abs(v - days) < Math.abs(best - days) ? v : best,
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

/**
 * Only one dialog is ever open, and the raw token lives solely inside the
 * 'reveal' variant — so closing that dialog is what drops the secret.
 */
type Dialog =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'reveal'; token: CreatedToken }
  | { kind: 'details'; token: PatSummary }
  | { kind: 'rotate'; token: PatSummary }
  | { kind: 'revoke'; token: PatSummary };

export const TokensPage = () => {
  const identityApi = useApi(identityApiRef);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  // See backendFetch in DashboardPage for why the bearer token is attached
  // explicitly rather than relying on fetchApi's own resolution.
  const backendFetch = useCallback(
    async (path: string, init?: RequestInit) => {
      const baseUrl = `${await discoveryApi.getBaseUrl('auth')}/tokens`;
      const { token } = await identityApi.getCredentials();
      return fetchApi.fetch(`${baseUrl}${path}`, {
        ...init,
        headers: {
          ...(init?.headers ?? {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    },
    [discoveryApi, fetchApi, identityApi],
  );

  const [tokens, setTokens] = useState<PatSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialog, setDialog] = useState<Dialog>({ kind: 'none' });
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [ttl, setTtl] = useState(DEFAULT_TTL);
  const [creating, setCreating] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [copied, setCopied] = useState(false);

  const menuRef = useRef<HTMLTableSectionElement | null>(null);

  /** Pulls the server's message out of a non-2xx response where there is one. */
  const readError = async (res: Response, fallback: string) => {
    try {
      const body = await res.json();
      return typeof body?.error === 'string' ? body.error : fallback;
    } catch {
      return fallback;
    }
  };

  const loadTokens = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await backendFetch('');
      if (!res.ok) {
        throw new Error(await readError(res, `HTTP ${res.status}`));
      }
      const data = await res.json();
      setTokens(data.tokens ?? []);
    } catch (err: any) {
      setError(err.message || 'Failed to load tokens');
    } finally {
      setLoading(false);
    }
  }, [backendFetch]);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  // Close the row action menu on any click outside the table body.
  useEffect(() => {
    if (!openMenuId) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openMenuId]);

  const nameValid = TOKEN_NAME_PATTERN.test(name);

  const openCreate = () => {
    setName('');
    setTtl(DEFAULT_TTL);
    setError(null);
    setDialog({ kind: 'create' });
  };

  const handleCreate = async () => {
    if (!nameValid || creating) return;
    setCreating(true);
    setError(null);
    setCopied(false);
    try {
      const res = await backendFetch('', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), expiresInDays: ttl }),
      });
      if (!res.ok) {
        throw new Error(await readError(res, `HTTP ${res.status}`));
      }
      setDialog({ kind: 'reveal', token: await res.json() });
      setName('');
      setTtl(DEFAULT_TTL);
      await loadTokens();
    } catch (err: any) {
      setError(err.message || 'Failed to create token');
    } finally {
      setCreating(false);
    }
  };

  const openRotate = (token: PatSummary) => {
    setTtl(originalTtl(token));
    setError(null);
    setCopied(false);
    setDialog({ kind: 'rotate', token });
  };

  const handleRotate = async (token: PatSummary) => {
    if (rotating) return;
    setRotating(true);
    setError(null);
    setCopied(false);
    try {
      const res = await backendFetch(`/${token.id}/rotate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expiresInDays: ttl }),
      });
      if (!res.ok) {
        throw new Error(await readError(res, `HTTP ${res.status}`));
      }
      setDialog({ kind: 'reveal', token: await res.json() });
      setTtl(DEFAULT_TTL);
      await loadTokens();
    } catch (err: any) {
      setError(err.message || 'Failed to rotate token');
    } finally {
      setRotating(false);
    }
  };

  const handleRevoke = async (token: PatSummary) => {
    if (revoking) return;
    setRevoking(true);
    setError(null);
    try {
      const res = await backendFetch(`/${token.id}`, { method: 'DELETE' });
      if (!res.ok) {
        throw new Error(await readError(res, `HTTP ${res.status}`));
      }
      setDialog({ kind: 'none' });
      await loadTokens();
    } catch (err: any) {
      setError(err.message || 'Failed to revoke token');
    } finally {
      setRevoking(false);
    }
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard is unavailable over plain http on some browsers; the value is
      // selectable on screen, so this is a convenience, not the only route.
      setError('Could not copy automatically — select the token and copy it.');
    }
  };

  /**
   * Discards the revealed token. Nothing else holds it and the list endpoint
   * never returns it, so this is genuinely one-way — hence the confirmation
   * when it has not been copied yet.
   */
  const closeReveal = () => {
    if (
      !copied &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        'You have not copied this token yet. Once this dialog closes the value cannot be shown again.\n\nClose anyway?',
      )
    ) {
      return;
    }
    setCopied(false);
    setDialog({ kind: 'none' });
  };

  const renderDialog = () => {
    if (dialog.kind === 'none') return null;

    let content: ReactNode = null;

    if (dialog.kind === 'create') {
      content = (
        <>
          <h2 style={S.modalTitle}>Create access token</h2>
          <p style={S.modalBlurb}>
            Access tokens let you call this portal's API without an interactive
            sign-in. The token is shown once, immediately after it is created.
          </p>
          {error && <div style={S.errBox}>⚠ {error}</div>}
          <div style={S.field}>
            <label style={S.label} htmlFor="pat-name">
              Name
            </label>
            <input
              id="pat-name"
              style={S.input}
              value={name}
              placeholder="e.g. my laptop, CI runner"
              maxLength={100}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleCreate();
              }}
            />
            <span style={name && !nameValid ? S.hintError : S.hint}>
              {name && !nameValid
                ? 'Must start with a letter or number and contain only letters, numbers, spaces, dots, underscores and hyphens (1–100 characters).'
                : 'Something you will recognise later, so you know what to revoke.'}
            </span>
          </div>
          <div style={S.field}>
            <label style={S.label} htmlFor="pat-ttl">
              Expires in
            </label>
            <select
              id="pat-ttl"
              style={{ ...S.input, width: 180 }}
              value={ttlToOption(ttl)}
              onChange={e => setTtl(optionToTtl(e.target.value))}
            >
              {TTL_OPTIONS.map(opt => (
                <option key={ttlToOption(opt.value)} value={ttlToOption(opt.value)}>
                  {opt.label}
                </option>
              ))}
            </select>
            {ttl === null && (
              <span style={S.hintWarn}>
                A token that never expires stops working only when you revoke
                it. Prefer a fixed expiry unless the automation cannot handle
                one.
              </span>
            )}
          </div>
          <div style={S.modalActions}>
            <button
              type="button"
              style={S.btnGhost}
              onClick={() => setDialog({ kind: 'none' })}
            >
              Cancel
            </button>
            <button
              type="button"
              style={{
                ...S.btn,
                ...(!nameValid || creating ? S.btnDisabled : {}),
              }}
              disabled={!nameValid || creating}
              onClick={handleCreate}
            >
              {creating ? 'Creating…' : 'Create'}
            </button>
          </div>
        </>
      );
    }

    if (dialog.kind === 'reveal') {
      const revealed = dialog.token;
      content = (
        <>
          <h2 style={S.modalTitle}>
            {revealed.rotatedFrom ? 'Copy your rotated token' : 'Copy your access token'}
          </h2>
          <div style={S.warnBanner}>
            This is the only time the token is shown. It is not stored anywhere,
            so once this dialog closes it cannot be retrieved — only revoked and
            replaced.
            {revealed.rotatedFrom && revealed.previousRevoked !== false
              ? ' The previous value has stopped working — update anything using it.'
              : ''}
          </div>
          {revealed.rotatedFrom && revealed.previousRevoked === false && (
            <div style={S.errBox}>
              ⚠ The replacement was created, but the previous token could not be
              revoked and still works. Revoke it from the list below.
            </div>
          )}
          <div style={S.revealValue}>
            <span style={S.revealToken}>{revealed.token}</span>
            <button
              type="button"
              style={S.btnGhost}
              onClick={() => handleCopy(revealed.token)}
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <div style={S.modalActions}>
            <button type="button" style={S.btn} onClick={closeReveal}>
              Done
            </button>
          </div>
        </>
      );
    }

    if (dialog.kind === 'details') {
      const t = dialog.token;
      content = (
        <>
          <h2 style={S.modalTitle}>{t.name}</h2>
          <p style={S.modalBlurb}>
            The token value is not stored and cannot be shown again.
          </p>
          <div style={S.dl}>
            <span style={S.dt}>Token</span>
            <p style={{ ...S.dd, ...S.mono }}>{MASKED_TOKEN}</p>
            <span style={S.dt}>ID</span>
            <p style={{ ...S.dd, ...S.mono }}>{t.id}</p>
            <span style={S.dt}>Created</span>
            <p style={S.dd}>{fmtDate(t.createdAt)}</p>
            <span style={S.dt}>Last accessed</span>
            <p style={S.dd}>{fmtDate(t.lastUsedAt)}</p>
            <span style={S.dt}>Expires</span>
            <p style={S.dd}>
              {t.expiresAt ? fmtDate(t.expiresAt) : 'Never'}
            </p>
          </div>
          <div style={S.modalActions}>
            <button
              type="button"
              style={S.btnGhost}
              onClick={() => setDialog({ kind: 'none' })}
            >
              Close
            </button>
          </div>
        </>
      );
    }

    if (dialog.kind === 'rotate') {
      const t = dialog.token;
      content = (
        <>
          <h2 style={S.modalTitle}>Rotate "{t.name}"?</h2>
          <p style={S.modalBlurb}>
            A new token value is issued under the same name and shown once. The
            current value stops working immediately, so anything using it must
            be updated.
          </p>
          {error && <div style={S.errBox}>⚠ {error}</div>}
          <div style={S.field}>
            <label style={S.label} htmlFor="pat-rotate-ttl">
              New token expires in
            </label>
            <select
              id="pat-rotate-ttl"
              style={{ ...S.input, width: 180 }}
              value={ttlToOption(ttl)}
              onChange={e => setTtl(optionToTtl(e.target.value))}
            >
              {TTL_OPTIONS.map(opt => (
                <option key={ttlToOption(opt.value)} value={ttlToOption(opt.value)}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span style={ttl === null ? S.hintWarn : S.hint}>
              {ttl === null
                ? 'A token that never expires stops working only when you revoke it.'
                : 'Defaults to the same expiry policy the current token was created with.'}
            </span>
          </div>
          <div style={S.modalActions}>
            <button
              type="button"
              style={S.btnGhost}
              onClick={() => setDialog({ kind: 'none' })}
            >
              Cancel
            </button>
            <button
              type="button"
              style={{ ...S.btn, ...(rotating ? S.btnDisabled : {}) }}
              disabled={rotating}
              onClick={() => handleRotate(t)}
            >
              {rotating ? 'Rotating…' : 'Rotate'}
            </button>
          </div>
        </>
      );
    }

    if (dialog.kind === 'revoke') {
      const t = dialog.token;
      content = (
        <>
          <h2 style={S.modalTitle}>Revoke "{t.name}"?</h2>
          <p style={S.modalBlurb}>
            Anything using this token stops working on its next request. This
            cannot be undone.
          </p>
          {error && <div style={S.errBox}>⚠ {error}</div>}
          <div style={S.modalActions}>
            <button
              type="button"
              style={S.btnGhost}
              onClick={() => setDialog({ kind: 'none' })}
            >
              Cancel
            </button>
            <button
              type="button"
              style={{
                ...S.btnDangerSolid,
                ...(revoking ? S.btnDangerDisabled : {}),
              }}
              disabled={revoking}
              onClick={() => handleRevoke(t)}
            >
              {revoking ? 'Revoking…' : 'Revoke'}
            </button>
          </div>
        </>
      );
    }

    return (
      <div style={S.overlay}>
        <div style={S.modal}>{content}</div>
      </div>
    );
  };

  return (
    <div style={S.page}>
      <div style={S.body}>
        {error && dialog.kind === 'none' && (
          <div style={S.errBox}>⚠ {error}</div>
        )}

        <div style={S.titleRow}>
          <span style={S.badge}>
            {tokens.length} active token{tokens.length === 1 ? '' : 's'}
          </span>
          <button type="button" style={S.btn} onClick={openCreate}>
            Create access token
          </button>
        </div>

        <div style={S.card}>
          {loading && <div style={S.center}>Loading…</div>}
          {!loading && tokens.length === 0 && (
            <div style={S.center}>No access tokens yet.</div>
          )}
          {!loading && tokens.length > 0 && (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Name</th>
                  <th style={S.th}>Created</th>
                  <th style={S.th}>Last accessed</th>
                  <th style={S.th}>Expires</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody ref={menuRef}>
                {tokens.map(t => {
                  const left = t.expiresAt ? daysUntil(t.expiresAt) : null;
                  return (
                    <tr key={t.id}>
                      <td style={S.tdName}>{t.name}</td>
                      <td style={S.td}>{fmtDate(t.createdAt)}</td>
                      <td style={S.td}>{fmtDate(t.lastUsedAt)}</td>
                      <td style={S.td}>
                        {t.expiresAt ? (
                          <>
                            {fmtDate(t.expiresAt)}{' '}
                            <span
                              style={{
                                ...S.pill,
                                ...(t.expired ? S.pillExpired : {}),
                              }}
                            >
                              {t.expired ? 'expired' : `${left ?? '?'}d left`}
                            </span>
                          </>
                        ) : (
                          <span style={{ ...S.pill, ...S.pillNever }}>
                            never expires
                          </span>
                        )}
                      </td>
                      <td style={S.actionsCell}>
                        <button
                          type="button"
                          style={S.kebab}
                          aria-label={`Actions for ${t.name}`}
                          aria-haspopup="menu"
                          aria-expanded={openMenuId === t.id}
                          onClick={() =>
                            setOpenMenuId(openMenuId === t.id ? null : t.id)
                          }
                        >
                          ⋯
                        </button>
                        {openMenuId === t.id && (
                          <div style={S.menu} role="menu">
                            <button
                              type="button"
                              role="menuitem"
                              style={S.menuItem}
                              onClick={() => {
                                setOpenMenuId(null);
                                setError(null);
                                setDialog({ kind: 'details', token: t });
                              }}
                            >
                              View details
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              style={S.menuItem}
                              onClick={() => {
                                setOpenMenuId(null);
                                openRotate(t);
                              }}
                            >
                              Rotate
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              style={{ ...S.menuItem, ...S.menuItemDanger }}
                              onClick={() => {
                                setOpenMenuId(null);
                                setError(null);
                                setDialog({ kind: 'revoke', token: t });
                              }}
                            >
                              Revoke
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {renderDialog()}
    </div>
  );
};
